<div class="record-detail bg-white border border-lightgrey rounded shadow-sm d-flex flex-column">
	<div class="d-flex align-items-center px-3 py-2 bg-light border-bottom">
		<span class="text-uppercase text-tiny text-secondary flex-weight-1">{{ kind }}</span>
		<button type="button" class="btn btn-sm btn-outline-secondary border-0 px-1" (click)="closed.emit()">
			<i class="wq wq-close"></i>
		</button>
	</div>

	<div class="px-3 pt-3">
		<div class="h4 mb-1">{{ title }}</div>
		<div class="text-secondary text-small">{{ subtitle }}</div>
	</div>

	<!-- SharedModule doesn't import UIModule, so this can't use ui-wait. -->
	<div *ngIf="loading" class="p-3">
		<div class="skeleton-line mb-2"></div>
		<div class="skeleton-line mb-2" style="width: 70%;"></div>
		<div class="skeleton-line" style="width: 40%;"></div>
	</div>

	<!-- A panel without sections is the caller's own content under the headline. -->
	<div *ngIf="!loading && !sectioned" class="record-items px-3 py-3">
		<ng-content select="[record-body]"></ng-content>
	</div>

	<ng-container *ngIf="!loading && sectioned">
	<div class="px-3 pt-3">
		<div class="d-flex align-items-baseline">
			<span class="h2 mb-0 mr-2">{{ headlineValue }}</span>
			<span class="text-uppercase text-tiny text-secondary">{{ headlineLabel }}</span>
		</div>
		<div class="text-secondary text-small">{{ summary }}</div>
	</div>

	<div class="px-3 pt-3 d-flex flex-wrap">
		<button *ngFor="let s of sections" type="button"
				class="btn btn-sm mr-2 mb-2 d-flex align-items-center"
				[ngClass]="s.id === activeSection ? 'btn-outline-primary' : 'btn-outline-secondary'"
				(click)="selectSection(s)">
			{{ s.name }}
			<span class="badge badge-pill ml-2" [ngClass]="s.id === activeSection ? 'badge-primary' : 'badge-lightergrey text-secondary'">{{ s.count }}</span>
		</button>
	</div>

	<div class="px-3" *ngIf="filterable">
		<input class="form-control form-control-sm bg-light border-0" type="search" [placeholder]="'Filter ' + (section?.name | lowercase) + '...'" [(ngModel)]="filter">
	</div>

	<div class="record-items px-3 py-3">
		<!-- Grouped: the records only read correctly under the heading they belong to, e.g. stock per warehouse. -->
		<ng-container *ngIf="isGrouped">
			<div *ngFor="let group of groups; trackBy: groupKey" class="mb-3">
				<!-- The sub-heading carries the gap where there is one, so this only takes it on when there isn't. -->
				<div class="d-flex align-items-center" [class.mb-2]="!group.meta">
					<!-- The heading opens the warehouse's stocked item list. -->
					<a *ngIf="itemLink(group); else plainGroup" class="text-bold text-primary flex-weight-1 text-truncate" target="_blank"
						[routerLink]="itemLink(group)" [title]="group.link_label">{{ group.name }}</a>
					<ng-template #plainGroup>
						<span class="text-bold text-primary flex-weight-1 text-truncate" [title]="group.name">{{ group.name }}</span>
					</ng-template>
					<span *ngIf="group.badge" class="badge text-tiny text-uppercase nowrap ml-2" [ngClass]="'badge-' + (group.badge_class || 'secondary')">{{ group.badge }}</span>
					<!-- The caller's action against this heading, e.g. reserving stock in the warehouse it names. -->
					<button *ngIf="showsGroupAction" type="button" class="btn btn-outline-info flex-shrink-0 nowrap ml-2"
						(click)="groupAction.emit(group)">{{ groupActionLabel }}</button>
				</div>
				<div *ngIf="group.meta" class="text-secondary text-tiny mb-2 text-truncate" [title]="group.meta">{{ group.meta }}</div>

				<div *ngFor="let line of group.lines" class="d-flex align-items-start py-1 border-top">
					<!-- Quantity is skipped where a line is one unit by definition, e.g. a serial. -->
					<span *ngIf="line.quantity !== null && line.quantity !== undefined" class="badge badge-lightergrey text-dark text-tiny nowrap mr-2">{{ line.quantity | decimal:0:4:true }}</span>
					<div class="flex-weight-1">
						<div class="d-flex align-items-center">
							<!-- The reference opens the record the line came from, e.g. the stock request behind an allocation. -->
							<a *ngIf="line.ref && refLink(line)" class="badge badge-lightergrey text-dark font-weight-normal mr-2 nowrap" target="_blank"
								[routerLink]="refLink(line)" [title]="line.ref_link_label">{{ line.ref }}</a>
							<span *ngIf="line.ref && !refLink(line)" class="badge badge-lightergrey text-dark font-weight-normal mr-2 nowrap">{{ line.ref }}</span>
							<!-- A location opens the stocked item list scoped to it; a commitment opens its project. -->
							<a *ngIf="itemLink(line); else plainLine" class="text-dark flex-weight-1 text-truncate" target="_blank"
								[routerLink]="itemLink(line)" [title]="line.link_label || line.title">{{ line.title }}</a>
							<ng-template #plainLine>
								<span class="text-dark flex-weight-1 text-truncate" [title]="line.title">{{ line.title }}</span>
							</ng-template>
							<span *ngIf="line.badge" class="badge text-tiny text-uppercase ml-2 nowrap" [ngClass]="'badge-' + (line.badge_class || 'secondary')">{{ line.badge }}</span>
						</div>
						<div *ngIf="line.meta" class="text-secondary text-tiny text-truncate" [title]="line.meta">{{ line.meta }}</div>
					</div>

					<!-- The caller's actions against this line, e.g. editing or dropping the reservation it describes. -->
					<div *ngIf="showsLineActions" class="d-flex flex-shrink-0 ml-2">
						<button *ngFor="let action of lineActions" type="button" class="btn btn-sm ml-1"
							[ngClass]="action.class" [title]="action.title"
							(click)="lineAction.emit({ action: action.id, line: line, group: group })">
							<i class="wq {{ action.icon }}"></i>
						</button>
					</div>
				</div>
			</div>

			<div *ngIf="!groups.length" class="text-secondary text-small">Nothing to show.</div>
		</ng-container>

		<!-- Flat: a simple list of records, e.g. a customer's quotes. -->
		<ng-container *ngIf="!isGrouped">
			<div class="text-uppercase text-tiny text-secondary mb-2">{{ section?.name }}</div>

			<div *ngFor="let item of items" class="mb-3 d-flex align-items-center">
				<div *ngIf="item.image_url" class="product-image flex-shrink-0 mr-2" [style.backgroundImage]="'url(\'' + item.image_url + '\')' | safeStyle"></div>

				<div class="flex-weight-1">
					<div class="d-flex align-items-center">
						<span *ngIf="item.ref" class="badge badge-lightergrey text-dark font-weight-normal mr-2 nowrap">{{ item.ref }}</span>
						<a *ngIf="itemLink(item); else plainTitle" class="text-dark flex-weight-1 text-truncate" target="_blank" [routerLink]="itemLink(item)" [title]="item.title">{{ item.title }}</a>
						<ng-template #plainTitle>
							<span class="text-dark flex-weight-1 text-truncate" [title]="item.title">{{ item.title }}</span>
						</ng-template>
						<span *ngIf="item.badge && !item.product_id" class="badge text-tiny text-uppercase ml-2 nowrap" [ngClass]="'badge-' + (item.badge_class || 'secondary')">{{ item.badge }}</span>
					</div>
					<div class="d-flex align-items-baseline">
						<span class="text-secondary text-small flex-weight-1 text-truncate" [title]="item.meta">{{ item.meta }}</span>
						<span *ngIf="item.amount !== null && item.amount !== undefined && !item.product_id" class="text-bold text-small ml-2 nowrap">{{ item.amount | currencyFlexible:(item.currency_id || currencyId) }}</span>
					</div>
					<div *ngIf="item.product_id" class="d-flex align-items-center mt-1">
						<span *ngIf="item.amount !== null && item.amount !== undefined" class="text-bold text-small nowrap mr-2">{{ item.amount | currencyFlexible:(item.currency_id || currencyId) }}</span>
						<span *ngIf="item.badge" class="badge text-tiny text-uppercase nowrap" [ngClass]="'badge-' + (item.badge_class || 'secondary')">{{ item.badge }}</span>
					</div>
				</div>

				<button *ngIf="canAddItem(item)" type="button" class="btn btn-sm add-btn text-tiny flex-shrink-0 ml-2 nowrap" [ngClass]="isAdded(item) ? 'btn-outline-success' : 'btn-secondary'" (click)="add.emit(item)">
					<i *ngIf="isAdded(item)" class="wq wq-check mr-1"></i>{{ isAdded(item) ? 'Added' : 'Add' }}
				</button>
			</div>

			<div *ngIf="!items.length" class="text-secondary text-small">Nothing to show.</div>
		</ng-container>
	</div>
	</ng-container>

	<div class="px-3 py-2 border-top" *ngIf="linkLabel && link && !linkAsButton">
		<a class="text-primary text-small" target="_blank" [routerLink]="link" [queryParams]="linkQuery">{{ linkLabel }} &rsaquo;</a>
	</div>
	<div class="p-3 border-top" *ngIf="linkLabel && link && linkAsButton && !loading">
		<a class="btn btn-primary btn-block" target="_blank" [routerLink]="link" [queryParams]="linkQuery">{{ linkLabel }} &rsaquo;</a>
	</div>
</div>
